import type { SubmissionSink } from '../sink'
import type { StepCommit } from '../machine'
import type { PhotoRef } from '@shared/schema/answers'
import { putOp, putPhoto, getPhoto } from './db'
import { createOp } from './outbox'
import { requestSync, getSyncState } from './sync'
import { newId } from '@shared/ids'

/**
 * ตัวเลือกการบีบอัดรูป
 * การ re-encode ผ่าน canvas ทำให้ EXIF orientation ถูกฝังลงในภาพจริง
 * (รูปจากมือถือจึงไม่ตะแคงตอนดูในหลังบ้าน) และตัด metadata ที่ไม่จำเป็นออก
 */
const COMPRESSION_OPTIONS = {
  maxWidthOrHeight: 2000,
  maxSizeMB: 0.8,
  fileType: 'image/jpeg',
  useWebWorker: true,
  initialQuality: 0.8,
}

/** URL ชั่วคราวของรูปที่ยังอยู่ในเครื่อง (ยังไม่ได้อัปโหลด) */
const localPhotoUrls = new Map<string, string>()

export function createQueueSink(submissionId: string, stepKeyRef: { current: string }): SubmissionSink {
  return {
    async ensureCreated(input) {
      await putOp(
        createOp('createSubmission', input.submissionId, {
          workflowId: input.workflowId,
          versionId: input.versionId,
          startedAt: input.startedAt,
        }),
      )
      requestSync()
    },

    async putStep(commit: StepCommit) {
      await putOp(
        createOp('step', commit.submissionId, {
          stepKey: commit.stepKey,
          nodeId: commit.nodeId,
          seq: commit.seq,
          answers: commit.answers,
          answeredAt: commit.answeredAt,
        }),
      )
      requestSync()
    },

    async complete(id: string, finalSeq: number, incomplete: boolean) {
      await putOp(createOp('complete', id, { finalSeq, completedAt: Date.now(), incomplete }))
      requestSync()
    },

    photo: {
      async capture(file: File, fieldName: string): Promise<PhotoRef> {
        const assetId = newId()
        let blob: Blob = file
        try {
          // โหลดตัวบีบอัดเมื่อถ่ายรูปครั้งแรกเท่านั้น — แบบสอบถามที่ไม่มีรูปจะไม่ต้องโหลดเลย
          const { default: imageCompression } = await import('browser-image-compression')
          blob = await imageCompression(file, COMPRESSION_OPTIONS)
        } catch (err) {
          // บีบอัดไม่ได้ (ไฟล์แปลก) — ส่งไฟล์ต้นฉบับไปแทน ดีกว่าทำงานพัง
          console.warn('บีบอัดรูปไม่สำเร็จ ใช้ไฟล์ต้นฉบับแทน', err)
        }

        await putPhoto({
          assetId,
          blob,
          contentType: blob.type || 'image/jpeg',
          submissionId,
          stepKey: stepKeyRef.current,
          fieldName,
          createdAt: Date.now(),
        })
        // เข้าคิวรูป "ก่อน" คำตอบของขั้นตอนเสมอ — ฝั่งเซิร์ฟเวอร์จึงมีไฟล์ก่อนถูกอ้างถึง
        await putOp(createOp('photo', submissionId, { assetId, fieldName }))

        localPhotoUrls.set(assetId, URL.createObjectURL(blob))
        requestSync()
        return { assetId, capturedAt: Date.now() }
      },

      urlFor(assetId: string): string {
        return localPhotoUrls.get(assetId) ?? `/api/assets/${assetId}`
      },

      pending(assetId: string): boolean {
        return localPhotoUrls.has(assetId)
      },
    },

    pendingCount: () => getSyncState().pending,
    lastError: () => getSyncState().lastError,
  }
}

/**
 * ล้าง URL ชั่วคราวของรูปที่อัปโหลดสำเร็จแล้ว
 * ต้องเรียกหลังอัปโหลดสำเร็จเสมอ ไม่งั้นป้าย "รอส่ง" จะค้างตลอดไป (เพราะ pending()
 * เช็คจาก map นี้) และ blob URL จะรั่วหน่วยความจำสะสมไปเรื่อย ๆ ตลอดการทำงานภาคสนาม
 */
export function clearLocalPhotoUrl(assetId: string): void {
  const url = localPhotoUrls.get(assetId)
  if (url) {
    URL.revokeObjectURL(url)
    localPhotoUrls.delete(assetId)
  }
}

/** คืน URL ของรูปที่ยังอยู่ในเครื่อง (ใช้ตอนกู้คืนงานหลังรีเฟรช) */
export async function restoreLocalPhotoUrl(assetId: string): Promise<string | null> {
  if (localPhotoUrls.has(assetId)) return localPhotoUrls.get(assetId) ?? null
  const record = await getPhoto(assetId)
  if (!record) return null
  const url = URL.createObjectURL(record.blob)
  localPhotoUrls.set(assetId, url)
  return url
}

export function releaseLocalPhotoUrls(): void {
  for (const url of localPhotoUrls.values()) URL.revokeObjectURL(url)
  localPhotoUrls.clear()
}
